import { TypedDocumentNode, gql } from '@apollo/client';
import { faArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Currency, UserAccountEntryEntry } from '__generated__/globalTypes';
import { FontAwesomeIcon } from 'atoms/icons';
import { LabelM } from 'atoms/typography';
import { getAccountIdentifier } from 'components/wallet/BankEthAccounting/Withdraw/WithdrawFiat/utils';
import { isType } from 'gql';

import { AccountEntry } from '../AccountEntry';
import { Withdrawal_userAccountEntry } from './__generated__/index.graphql';

const messages = defineMessages({
  title: {
    id: 'TransactionsHistoryWithdrawal.title',
    defaultMessage: 'Withdrawal',
  },
  titleFees: {
    id: 'TransactionsHistoryWithdrawal.titleFees',
    defaultMessage: 'Withdrawal fees',
  },
  detail: {
    id: 'TransactionsHistoryWithdrawal.detail',
    defaultMessage: 'Withdrawal to {address}',
  },
});

type Props = {
  userAccountEntry: Withdrawal_userAccountEntry;
  withIcon?: boolean;
  expandable?: boolean;
  primaryCurrency: Currency;
};

type WithdrawalType = NonNullable<
  Withdrawal_userAccountEntry['tokenOperation']
> & { __typename: 'Withdrawal' | 'FastWithdrawal' | 'MangopayWithdrawal' };

export const isAToWithdrawal = function isAWithdrawal(item: {
  __typename: string;
  to?: string;
}): item is WithdrawalType {
  return (
    ['Withdrawal', 'FastWithdrawal', 'MangopayWithdrawal'].includes(
      item?.__typename
    ) && !!item.to
  );
};

export const WithdrawalIcon = styled(FontAwesomeIcon)`
  width: var(--double-unit);
  height: var(--double-unit);
  border-radius: 50%;
  align-self: center;
  background-color: transparent;
  border: 1px solid var(--c-white);
  color: var(--c-white);
  padding: var(--unit);
`;

export const Withdrawal = ({
  userAccountEntry,
  withIcon,
  expandable,
  primaryCurrency,
}: Props) => {
  const { tokenOperation } = userAccountEntry;
  const { formatMessage } = useIntl();

  const destination = useMemo(() => {
    if (!tokenOperation || !isAToWithdrawal(tokenOperation)) return null;

    if (!isType(tokenOperation, 'MangopayWithdrawal')) {
      return tokenOperation.to;
    }

    return getAccountIdentifier(tokenOperation.bankAccount);
  }, [tokenOperation]);

  if (
    ![
      UserAccountEntryEntry.WITHDRAWAL,
      UserAccountEntryEntry.WITHDRAWAL_FEE,
    ].includes(userAccountEntry.entryType) ||
    !destination
  )
    return null;

  return (
    <AccountEntry
      expandable={expandable}
      userAccountEntry={userAccountEntry}
      title={
        userAccountEntry.entryType === UserAccountEntryEntry.WITHDRAWAL_FEE
          ? formatMessage(messages.titleFees)
          : formatMessage(messages.title)
      }
      {...(withIcon && { icon: <WithdrawalIcon icon={faArrowUpRight} /> })}
      primaryCurrency={primaryCurrency}
    >
      <LabelM>
        <FormattedMessage
          {...messages.detail}
          values={{ address: destination }}
        />
      </LabelM>
    </AccountEntry>
  );
};

Withdrawal.fragments = {
  userAccountEntry: gql`
    fragment Withdrawal_userAccountEntry on UserAccountEntry {
      id
      entryType
      tokenOperation {
        ... on Node {
          id
        }
        ... on Withdrawal {
          id
          to
        }
        ... on FastWithdrawal {
          id
          to
        }
        ... on MangopayWithdrawal {
          id
          to
          bankAccount {
            ... on BankAccountInterface {
              id
            }
            ...getAccountIdentifier_bankAccount
          }
        }
      }
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
    ${getAccountIdentifier.fragments.bankAccount}
  ` as TypedDocumentNode<Withdrawal_userAccountEntry>,
};
